import React, { useState, useRef, useEffect } from "react";
import useInfoAlert from "../../hooks/useInfoAlert";
import InfoAlert from "./InfoAlert";

const SimpleCircularUpload = ({
  defaultImage = null,
  currentImage = null,
  size = 120,
  maxFileSize = 5, // MB
  onChange = () => {},
  disabled = false,
}) => {
  const [image, setImage] = useState(currentImage || defaultImage);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef(null);
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  // Add CSS styles
  useEffect(() => {
    const styles = `
      .profile-upload {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        overflow: hidden;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 3px solid #e5e7eb;
        background-color: #f3f4f6;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-sizing: border-box;
      }

      .profile-upload:hover {
        border-color: #d1d5db;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
      }

      .profile-upload.disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      .profile-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .profile-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        line-height: 1.15;
        padding: 0 0.35rem;
        box-sizing: border-box;
        background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
        color: #9ca3af;
        font-weight: 500;
      }

      .profile-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 15px;
        opacity: 0;
        transition: opacity 0.3s ease;
        backdrop-filter: blur(2px);
      }

      .profile-upload:hover .profile-overlay {
        opacity: 1;
      }

      .profile-action-btn {
        width: 25px;
        height: 25px;
        border: none;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 14px;
        color: white;
      }

      .profile-change-btn {
        background: #3b82f6;
      }

      .profile-change-btn:hover {
        background: #2563eb;
        transform: scale(1.1);
      }

      .profile-remove-btn {
        background: #ef4444;
      }

      .profile-remove-btn:hover {
        background: #dc2626;
        transform: scale(1.1);
      }

      .profile-input {
        display: none;
      }
    `;

    const styleId = "profile-upload-styles";
    let styleSheet = document.getElementById(styleId);
    if (!styleSheet) {
      styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      document.head.appendChild(styleSheet);
    }
    styleSheet.innerText = styles;
  }, []);

  // Update image when props change
  useEffect(() => {
    setImage(currentImage || defaultImage);
  }, [currentImage, defaultImage]);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Validate file
  const validateFile = (file) => {
    if (!file.type.startsWith("image/")) {
      throw new Error("Please select an image file");
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      throw new Error(`File size must be less than ${maxFileSize}MB`);
    }

    return true;
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    try {
      validateFile(file);
      const base64 = await fileToBase64(file);
      setImage(base64);
      onChange(base64);
    } catch (error) {
      showAlert({
        type: "warning",
        title: "Unable to change image",
        message: error.message || "Please choose a valid image file.",
      });
    }
  };

  // Handle change image click
  const handleChangeClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Handle remove click
  const handleRemoveClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      setImage(defaultImage);
      onChange("" || null);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Add this line to clear the input value
    e.target.value = "";
  };

  // Handle container click
  const handleContainerClick = () => {
    if (!disabled && !isHovered) {
      fileInputRef.current?.click();
    }
  };

  const placeholderFontSize = Math.max(9, Math.min(14, Math.round(size * 0.17)));

  return (
    <div
      className={`profile-upload ${disabled ? "disabled" : ""}`}
      style={{ width: size, height: size }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleContainerClick}
    >
      {image ? (
        <img src={image} alt="Profile" className="profile-image" />
      ) : (
        <div
          className="profile-placeholder"
          style={{ fontSize: `${placeholderFontSize}px` }}
        >
          No Image
        </div>
      )}

      {!disabled && (
        <div className="profile-overlay">
          <button
            className="profile-action-btn profile-change-btn"
            onClick={handleChangeClick}
            title="Change Image"
          >
            <i className="fas fa-camera"></i>
          </button>

          {image &&
            image !== defaultImage &&
            !String(currentImage || "").includes(
              "ascensionblob.blob.core.windows.net",
            ) && (
              <button
                className="profile-action-btn profile-remove-btn"
                onClick={handleRemoveClick}
                title="Remove Image"
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="profile-input"
        disabled={disabled}
      />
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default SimpleCircularUpload;
